import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getPickingOrderRequest,
  getPickingOrdersRequest,
  pickRequest,
  reservePickingOrderRequest,
  shortPickRequest,
  unpickRequest,
} from './operatorPickingApi';
import {
  PICKING_STEP,
  buildPickBody,
  buildShortPickBody,
  buildUnpickBody,
  matchSuggestion,
  otherLineFor,
} from './operatorPickingUtils';
import { pickingLineSwitched } from './operatorPickingActions';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { matchCodes } from '../operator/operatorCodes';
import { OPERATOR_ERROR, localError, readOperatorError } from '../operator/operatorErrors';
import { idempotentThunkOptions, sameAction, sendWrite } from '../operator/operatorIdempotency';
import { SCAN_GATE, cleanCode, scanGate } from '../operator/operatorScanGate';
import { offScreenMeta } from '../operator/operatorTaskScope';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';

const warehouseOf = (getState) => selectCurrentOperatorWarehouseId(getState());

const currentLineOf = (picking) =>
  (picking.order?.lines || []).find((line) => line.line_id === picking.walk.lineId) || null;

/** The order as its screen titled it: «ORD-50», or its number if it has no reference. */
const orderName = (picking, orderId) => picking.names[orderId] || `Orden #${orderId}`;

const writeOptions = {
  ...idempotentThunkOptions,
  // Keyed by order, so a write left out on one does not keep the next one's buttons busy.
  getPendingMeta: ({ arg }) => ({ pendingKey: arg?.orderId }),
};

export const fetchPickingQueue = createAsyncThunk(
  'operatorPicking/fetchQueue',
  async ({ page = 1 } = {}, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await getPickingOrdersRequest(warehouseOf(getState), { page });
      return fulfillWithValue(data, { receivedAt: Date.now() });
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar las órdenes por pickear.'));
    }
  }
);

export const fetchPickingOrder = createAsyncThunk(
  'operatorPicking/fetchOrder',
  async (orderId, { getState, rejectWithValue }) => {
    try {
      const { data } = await getPickingOrderRequest(warehouseOf(getState), orderId);
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos abrir la orden.'));
    }
  }
);

/**
 * Where a write's answer lands, read when it arrives. An order off screen
 * gets the band named after it (`meta.offScreenTask`) and is not reloaded.
 * The order on screen takes the answer only if it is the write its visit
 * waits for; any other — sent on an earlier visit — reloads it instead, so
 * the screen shows how the order stands. The answer only brings its line,
 * so a reload already out when the awaited one lands is asked again.
 *
 * @param {{getState: Function, dispatch: Function, requestId: string}} thunkApi
 * @param {number} orderId
 */
const answerMeta = ({ getState, dispatch, requestId }, orderId) => {
  const state = getState();
  const picking = state.operatorPicking;
  const meta = offScreenMeta(state, OPERATOR_FLOW.PICKING, orderId, orderName(picking, orderId));
  if (meta) return meta;
  const awaited = picking.lastWrite?.key === requestId;
  // A reload still out read the order before this answer and would undo it: ask again after it.
  if (!awaited || picking.loading) dispatch(fetchPickingOrder(orderId));
  return awaited ? undefined : { earlierVisit: true };
};

/**
 * A code read on the walk: first the slot, then the product. The slot can be
 * any of the line's suggestions — or another, which the pick will check — and
 * the product says in which unit the quantity is.
 *
 * @param {{code: string, source: string, alternatives?: string[]}} read - `alternatives`: the other codes in view.
 */
export const scanPickingCode = createAsyncThunk(
  'operatorPicking/scanCode',
  async ({ code, alternatives = [] }, { getState, fulfillWithValue, rejectWithValue }) => {
    const picking = getState().operatorPicking;
    const line = currentLineOf(picking);
    const value = cleanCode(code);

    if (picking.walk.step === PICKING_STEP.SLOT) {
      // A slot label beside the product may be smaller on screen, and the camera will not report it again.
      const matched = [value, ...alternatives].find((candidate) => matchSuggestion(line, candidate));
      if (matched) {
        return fulfillWithValue({ kind: 'slot', target: matchSuggestion(line, matched), suggested: true }, { matched });
      }

      const product = otherLineFor(picking.order.lines, null, value);
      if (product) return rejectWithValue(localError(`${product.sku} es un producto: primero escanea la ubicación.`));

      return {
        kind: 'slot',
        target: { slot_code: value, slot_name: value, available: null, lots: [] },
        suggested: false,
      };
    }

    const match = matchCodes(line?.codes, value);
    if (match) return { kind: 'product', product: { code: value, factor: match.factor } };

    const other = otherLineFor(picking.order.lines, line?.line_id, value);
    return rejectWithValue(
      localError(
        other ? `Ese código es de ${other.sku}; ahora toca ${line.sku}.` : `${value} no es ${line?.sku || 'este producto'}.`
      )
    );
  },
  {
    condition: (read, { getState }) => {
      const picking = getState().operatorPicking;
      const busy =
        !picking.order ||
        Boolean(picking.pending || picking.resolution) ||
        ![PICKING_STEP.SLOT, PICKING_STEP.PRODUCT].includes(picking.walk.step);
      return scanGate({ busy, lastCode: picking.scan.lastCode }, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * Takes units of a line from a slot.
 *
 * @param {{orderId: number, lineId: number, productCode: string, slotCode: string, slotName?: string,
 *   quantity: number, factor: number, lot?: string, idempotencyKey?: string}} arg
 */
export const pickLine = createAsyncThunk(
  'operatorPicking/pick',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await pickRequest(warehouseOf(getState), arg.orderId, buildPickBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg.orderId));
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos registrar el pick.'), answerMeta(thunkApi, arg.orderId));
    }
  },
  writeOptions
);

/**
 * Reports finding less than the system says in a slot. The server opens a
 * count task and answers the line with that slot out of its suggestions.
 *
 * @param {{orderId: number, lineId: number, slotCode: string, slotName?: string, found: number,
 *   reason: string, productCode?: string, lot?: string, notes?: string, idempotencyKey?: string}} arg
 */
export const shortPickLine = createAsyncThunk(
  'operatorPicking/shortPick',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await shortPickRequest(warehouseOf(getState), arg.orderId, buildShortPickBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg.orderId));
    } catch (error) {
      return rejectWithValue(
        readOperatorError(error, 'No pudimos registrar el desvío.'),
        answerMeta(thunkApi, arg.orderId)
      );
    }
  },
  writeOptions
);

/**
 * Puts picked units back in a slot.
 *
 * @param {{orderId: number, lineId: number, slotCode: string, quantity: number, lot?: string,
 *   reason?: string, idempotencyKey?: string}} arg
 */
export const unpickLine = createAsyncThunk(
  'operatorPicking/unpick',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await unpickRequest(warehouseOf(getState), arg.orderId, buildUnpickBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg.orderId));
    } catch (error) {
      return rejectWithValue(
        readOperatorError(error, 'No pudimos devolver esas unidades.'),
        answerMeta(thunkApi, arg.orderId)
      );
    }
  },
  writeOptions
);

/**
 * Reserves what the order still lacks, as far as there is free stock, for a
 * client whose contract does not reserve on entry. Optional: the walk goes on
 * without it, and sent again it reserves nothing twice. A refusal — the
 * contract reserves by itself now, or the order left picking — reloads the
 * order on screen, so the button goes.
 *
 * @param {{orderId: number, idempotencyKey?: string}} arg
 */
export const reservePickingOrder = createAsyncThunk(
  'operatorPicking/reserve',
  async (arg, thunkApi) => {
    const { getState, dispatch, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await reservePickingOrderRequest(warehouseOf(getState), arg.orderId, { idempotency_key: requestId });
      return fulfillWithValue(data, answerMeta(thunkApi, arg.orderId));
    } catch (error) {
      const failure = readOperatorError(error, 'No pudimos reservar el stock.');
      const meta = answerMeta(thunkApi, arg.orderId);
      if (!meta && failure.kind === OPERATOR_ERROR.REFUSED && !getState().operatorPicking.loading) {
        dispatch(fetchPickingOrder(arg.orderId));
      }
      return rejectWithValue(failure, meta);
    }
  },
  writeOptions
);

const pickingFlow = (state) => state.operatorPicking;

/** The walk's writes as its screen sends them: the same action after a lost answer keeps its key. */
export const submitPick = (arg) => sendWrite(pickLine, arg, pickingFlow);
export const submitShortPick = (arg) => sendWrite(shortPickLine, arg, pickingFlow);
export const submitUnpick = (arg) => sendWrite(unpickLine, arg, pickingFlow);
export const submitReserve = (arg) => sendWrite(reservePickingOrder, arg, pickingFlow);

// A lost answer's write goes again from the band, which carries it (`workspaces/operator/operatorRetry`).
const WRITES = {
  [pickLine.typePrefix]: pickLine,
  [shortPickLine.typePrefix]: shortPickLine,
  [unpickLine.typePrefix]: unpickLine,
};

/**
 * What the operator chose for a 409: the line the code was, or the lot. The
 * same write goes again with it and the same key.
 *
 * @param {number|string} id
 */
export const choosePickingOption = (id) => (dispatch, getState) => {
  const { resolution } = getState().operatorPicking;
  if (!resolution || resolution.kind !== 'ambiguous') return null;
  const thunk = WRITES[resolution.type];
  if (!thunk) return null;

  if (resolution.field === 'line_id') {
    dispatch(pickingLineSwitched(id));
    return dispatch(thunk(sameAction({ arg: { ...resolution.arg, lineId: id }, key: resolution.key })));
  }
  return dispatch(thunk(sameAction({ arg: { ...resolution.arg, lot: id }, key: resolution.key })));
};
